import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, UserMinus, PauseCircle, Lock, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';

export default function AccountDeletion() {
  const navigate = useNavigate();
  const { logout } = useAuth(); 
  
  const [step, setStep] = useState(1);
  const [deletionType, setDeletionType] = useState(null);
  const [reason, setReason] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  // Initialized strictly blank
  const [credentials, setCredentials] = useState({ 
    email: "", 
    password: "" 
  });
  const [captcha, setCaptcha] = useState({ num1: 0, num2: 0, input: "" });

  const reasons = [
    "I don't use the app anymore",
    "I found a better alternative",
    "I have privacy concerns",
    "Too many technical issues or bugs",
    "I get too many notifications",
    "Other"
  ];

  // Captcha generates once on mount
  useEffect(() => {
    generateCaptcha();
  }, []);

  const generateCaptcha = () => {
    setCaptcha({
      num1: Math.floor(Math.random() * 9) + 1,
      num2: Math.floor(Math.random() * 9) + 1,
      input: ""
    });
  };

  const isAuthValid = 
    credentials.email.trim().includes("@") && 
    credentials.password.length > 0 && 
    parseInt(captcha.input, 10) === (captcha.num1 + captcha.num2);

  const handleFinalAction = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    
    try {
      const token = 
        localStorage.getItem("token") || 
        localStorage.getItem("access_token") || 
        localStorage.getItem("authToken");

      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

      await api.post('/auth/delete-account', { 
        email: credentials.email.trim().toLowerCase(), 
        password: credentials.password, 
        type: deletionType,
        reason: reason 
      }, {
        headers: authHeaders
      });

      try {
        if (logout) await logout();
      } catch (e) {
        // Suppress cleanup errors
      }
      localStorage.clear();
      sessionStorage.clear();

      if (deletionType === 'temp') {
        toast.success("Account deactivated. It will be permanently removed in 30 days unless you log back in.");
      } else {
        toast.success("Account permanently deleted. All your data has been wiped.");
      }
      
      navigate("/auth/login", { replace: true }); 
      
    } catch (error) {
      const errorMessage = 
        error.response?.data?.detail || 
        error.response?.data?.message ||
        "Verification failed. Please check your credentials and try again.";
      toast.error(errorMessage);
      generateCaptcha();
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-4 py-12 bg-gray-50 sm:px-6 lg:px-8">
      
      <div className="w-full max-w-xl p-8 bg-white border border-gray-200 shadow-xl rounded-3xl">
        
        {/* Header Navigation */}
        <div className="flex items-center mb-8">
          <button 
            type="button"
            onClick={() => step > 1 ? setStep(step - 1) : navigate(-1)} 
            className="p-2 mr-4 text-gray-400 transition rounded-full hover:bg-gray-100 hover:text-gray-900 cursor-pointer"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-black text-gray-900">
            {step === 1 && "Manage Account"}
            {step === 2 && "Why are you leaving?"}
            {step === 3 && "Verify Identity"}
            {step === 4 && "Final Confirmation"}
          </h1>
        </div>

        {/* STEP 1: Choose Type */}
        {step === 1 && (
          <div className="space-y-4 animate-in fade-in slide-in-from-right-4">
            <p className="mb-6 text-gray-600">Please select what you would like to do with your ExpoLead account.</p>
            
            <button 
              type="button"
              onClick={() => { setDeletionType('temp'); setStep(2); }}
              className="flex items-start w-full gap-4 p-5 text-left transition border border-gray-200 shadow-xs rounded-2xl hover:border-blue-400 hover:shadow-md group cursor-pointer"
            >
              <PauseCircle className="w-6 h-6 mt-1 text-blue-500 shrink-0" />
              <div>
                <h3 className="text-lg font-bold text-gray-900 transition group-hover:text-blue-600">Deactivate temporarily (30 Days)</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Your profile and leads will be hidden from team stats. <strong>If you log back in within 30 days, your account will be reactivated. Otherwise, it will be deleted permanently.</strong>
                </p>
              </div>
            </button>

            <button 
              type="button"
              onClick={() => { setDeletionType('perm'); setStep(2); }}
              className="flex items-start w-full gap-4 p-5 text-left transition border border-gray-200 shadow-xs rounded-2xl hover:border-red-400 hover:shadow-md group cursor-pointer"
            >
              <UserMinus className="w-6 h-6 mt-1 text-red-500 shrink-0" />
              <div>
                <h3 className="text-lg font-bold text-gray-900 transition group-hover:text-red-600">Delete permanently</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Your profile, all captured leads, passes, and settings will be permanently erased immediately. This cannot be undone.
                </p>
              </div>
            </button>
          </div>
        )}

        {/* STEP 2: Feedback Survey */}
        {step === 2 && (
          <div className="animate-in fade-in slide-in-from-right-4">
            <p className="mb-6 text-gray-600">
              We are always trying to improve. Please let us know why you are {deletionType === 'temp' ? 'deactivating' : 'deleting'} your account.
            </p>
            
            <div className="mb-8 space-y-3">
              {reasons.map((r, idx) => (
                <label key={idx} className={`flex items-center p-4 border rounded-xl cursor-pointer transition ${reason === r ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                  <input 
                    type="radio" 
                    name="reason" 
                    value={r} 
                    checked={reason === r} 
                    onChange={(e) => setReason(e.target.value)} 
                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 cursor-pointer"
                  />
                  <span className="ml-3 font-medium text-gray-900">{r}</span>
                </label>
              ))}
            </div>
            
            <button 
              type="button"
              onClick={() => setStep(3)}
              disabled={!reason}
              className="w-full py-3.5 font-bold text-white transition bg-gray-900 rounded-xl hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              Continue
            </button>
          </div>
        )}

        {/* STEP 3: Identity Verification & Captcha */}
        {step === 3 && (
          <div 
            onKeyDown={(e) => {
              if (e.key === 'Enter' && isAuthValid) {
                e.preventDefault();
                setStep(4);
              }
            }}
            className="animate-in fade-in slide-in-from-right-4"
          >
            {/* Hidden decoy fields to absorb aggressive browser credential autofill */}
            <div 
              style={{ position: 'absolute', opacity: 0, height: 0, width: 0, overflow: 'hidden', zIndex: -1 }} 
              aria-hidden="true"
            >
              <input type="text" name="decoy_username" tabIndex={-1} autoComplete="username" />
              <input type="password" name="decoy_password" tabIndex={-1} autoComplete="current-password" />
            </div>

            <div className="p-4 mb-6 border border-yellow-200 bg-yellow-50 rounded-xl">
              <div className="flex items-start gap-3">
                <Lock className="w-5 h-5 text-yellow-600 shrink-0 mt-0.5" />
                <p className="text-sm text-yellow-800">
                  For your security, please verify your credentials before proceeding.
                </p>
              </div>
            </div>

            <div className="space-y-4 mb-8">
              <div>
                <label className="block mb-1.5 text-sm font-bold text-gray-700">Account Email ID</label>
                <input 
                  type="text" 
                  inputMode="email"
                  name="identity_verification_email"
                  id="identity_verification_email"
                  autoComplete="one-time-code"
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck="false"
                  readOnly
                  onFocus={(e) => e.target.removeAttribute('readonly')}
                  placeholder="Enter your account email"
                  value={credentials.email}
                  onChange={(e) => {
                    const text = e.target.value;
                    setCredentials((prev) => ({ ...prev, email: text }));
                  }}
                  className="w-full px-4 py-3 text-base font-medium transition-shadow border bg-gray-50 border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:bg-white"
                />
              </div>
              
              <div>
                <label className="block mb-1.5 text-sm font-bold text-gray-700">Password</label>
                <input 
                  type="password" 
                  name="identity_verification_pwd"
                  id="identity_verification_pwd"
                  autoComplete="new-password"
                  readOnly
                  onFocus={(e) => e.target.removeAttribute('readonly')}
                  placeholder="Enter your password"
                  value={credentials.password}
                  onChange={(e) => {
                    const text = e.target.value;
                    setCredentials((prev) => ({ ...prev, password: text }));
                  }}
                  className="w-full px-4 py-3 text-base font-medium transition-shadow border bg-gray-50 border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:bg-white"
                />
              </div>

              {/* Math Captcha */}
              <div className="pt-4 mt-4 border-t border-gray-100">
                <label className="block mb-1.5 text-sm font-bold text-gray-700">Security Check</label>
                <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center px-4 py-3 font-mono text-lg font-bold tracking-widest text-blue-900 bg-blue-100 border border-blue-200 rounded-xl select-none">
                    {captcha.num1} + {captcha.num2} = ?
                  </div>
                  <input 
                    type="number" 
                    name="captcha_ans"
                    autoComplete="off"
                    placeholder="Answer"
                    value={captcha.input}
                    onChange={(e) => {
                      const text = e.target.value;
                      setCaptcha((prev) => ({ ...prev, input: text }));
                    }}
                    className="flex-1 px-4 py-3 text-base font-medium transition-shadow border bg-gray-50 border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:bg-white"
                  />
                </div>
              </div>
            </div>

            <button 
              type="button"
              onClick={() => { if (isAuthValid) setStep(4); }}
              disabled={!isAuthValid}
              className="w-full py-3.5 font-bold text-white transition bg-gray-900 rounded-xl hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              Verify Identity
            </button>
          </div>
        )}

        {/* STEP 4: Final Confirmation */}
        {step === 4 && (
          <div className="animate-in fade-in slide-in-from-right-4">
            {deletionType === 'perm' ? (
              <div className="p-6 mb-8 border border-red-200 bg-red-50 rounded-2xl">
                <div className="flex items-center gap-3 mb-4 text-red-700">
                  <ShieldAlert className="w-8 h-8 shrink-0" />
                  <h3 className="text-xl font-bold">Are you absolutely sure?</h3>
                </div>
                <p className="text-red-900 leading-relaxed text-sm">
                  You are about to execute a permanent deletion of the account <strong>{credentials.email}</strong>. All captured leads, passes, team associations, and settings will be wiped immediately.
                  <br/><br/>
                  <strong>This action cannot be undone under any circumstances.</strong>
                </p>
              </div>
            ) : (
              <div className="p-6 mb-8 border border-blue-200 bg-blue-50 rounded-2xl">
                <h3 className="mb-2 text-xl font-bold text-blue-900">Ready to take a break?</h3>
                <p className="text-blue-800 leading-relaxed text-sm">
                  Your account ({credentials.email}) will be deactivated and hidden from team stats for <strong>30 days</strong>. If you log back in during this period, your account will be reactivated automatically. Otherwise, it will be deleted permanently.
                </p>
              </div>
            )}

            <div className="flex flex-col gap-3">
              <button 
                type="button"
                onClick={handleFinalAction}
                disabled={isProcessing}
                className={`w-full py-4 font-bold text-white transition rounded-xl flex justify-center items-center gap-2 shadow-lg cursor-pointer ${
                  deletionType === 'perm' 
                    ? 'bg-red-600 hover:bg-red-700 shadow-red-600/30' 
                    : 'bg-blue-600 hover:bg-blue-700 shadow-blue-600/30'
                } disabled:opacity-50`}
              >
                {isProcessing 
                  ? "Processing Request..." 
                  : deletionType === 'perm' 
                    ? "Yes, Permanently Delete My Account" 
                    : "Yes, Deactivate Account"}
              </button>
              
              <button 
                type="button"
                onClick={() => navigate("/")}
                disabled={isProcessing}
                className="w-full py-3.5 font-bold text-gray-700 transition bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50 cursor-pointer"
              >
                Cancel and Return to Home
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}